import type { ChangeEvent, DragEvent, FormEvent } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  FileSpreadsheet,
  Info,
  Upload,
  PlusCircle,
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { canApproveProducts, isProfileComplete } from '../utils/businessLevelLabels'
import {
  startUnifiedProductImportJob,
  fetchUnifiedProductImportJob,
  previewUnifiedProductImport,
  upsertTermReference,
  type UnifiedProductImportPreviewDto,
  type UnifiedProductImportResultDto,
} from '../services/api'
import { EmptyState, MimCard, MimPage, StatusPill } from '../components/MimUi'

const additionalFieldCategories = ['Product', 'Packaging', 'Technical', 'Marketing', 'Metadata', 'Certification', 'Fire', 'Approval']
const schemaCategoryOrder = ['Product', 'Packaging', 'Marketing', 'Technical', 'Fire', 'Certification', 'Approval', 'Metadata', 'Downloads']

export default function PCRUpload() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [preview, setPreview] = useState<UnifiedProductImportPreviewDto | null>(null)
  const [result, setResult] = useState<UnifiedProductImportResultDto | null>(null)
  const [selectedSheets, setSelectedSheets] = useState<Record<string, boolean>>({})
  const [jobId, setJobId] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [additionalField, setAdditionalField] = useState({
    productFamily: '',
    category: 'Technical',
    name: '',
    aliases: '',
  })
  const [additionalFields, setAdditionalFields] = useState<Array<{ productFamily: string; category: string; name: string }>>([])
  const [approveOnImport, setApproveOnImport] = useState(false)

  const owner = user?.displayName || user?.email || user?.userId || ''
  const isGroupUser = (user?.businessLevel || '').toLowerCase() === 'masteradmin'
  const divisionLabel = user?.divisionName
    || user?.subdivisionName
    || user?.businessEntityNames?.[0]
    || (isGroupUser ? (user?.divisionId ? 'Division from profile' : 'No division selected') : 'Assigned from profile')
  const profileReady = !!user && isProfileComplete(user)
  const canApprove = canApproveProducts(user)
  const requestApproveOnImport = canApprove && approveOnImport

  const previewMutation = useMutation({
    mutationFn: (nextFile: File) => previewUnifiedProductImport(nextFile),
    onMutate: () => {
      setMessage('Reading file and detecting fields...')
      setPreview(null)
      setResult(null)
      setJobId(null)
    },
    onSuccess: (nextPreview) => {
      setPreview(nextPreview)
      setSelectedSheets(
        Object.fromEntries(nextPreview.sheets.map((sheet) => [sheet.sheetName, sheet.isDefaultSelected]))
      )
      setMessage(`${nextPreview.selectedDataRows} import rows detected across ${nextPreview.sheets.length} sheet(s).`)
    },
    onError: (error: any) => {
      setMessage(importErrorMessage(error, 'Preview failed. Check the file format and try again.'))
    },
  })

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!profileReady) throw new Error('Complete your profile before importing.')
      if (!file || !preview) throw new Error('Choose and preview a file first.')
      return startUnifiedProductImportJob(
        preview.uploadId ? null : file,
        'merge',
        {
          sheets: preview.sheets.map((sheet) => ({
            sheetName: sheet.sheetName,
            selected: selectedSheets[sheet.sheetName] ?? sheet.isDefaultSelected,
            mappings: sheet.inferredMappings,
          })),
        },
        user?.divisionId || undefined,
        owner,
        requestApproveOnImport,
        preview.uploadId,
        file.name,
      )
    },
    onMutate: () => {
      setMessage('Starting import job...')
      setResult(null)
    },
    onSuccess: (started) => {
      setJobId(started.jobId)
      setMessage('Import running in the background. Stay on this page.')
    },
    onError: (error: any) => {
      setMessage(importErrorMessage(error))
    },
  })

  const { data: job } = useQuery({
    queryKey: ['product-import-job', jobId],
    queryFn: () => fetchUnifiedProductImportJob(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (!status) return 1500
      if (status === 'Completed' || status === 'Failed') return false
      return 1500
    },
  })

  useEffect(() => {
    if (!job) return
    if (job.status === 'Completed' && job.result) {
      setResult(job.result)
      setMessage('Import completed.')
      qc.invalidateQueries({ queryKey: ['products'] })
      qc.invalidateQueries({ queryKey: ['product-pim'] })
    } else if (job.status === 'Failed') {
      setMessage(job.errorMessage || 'Import failed.')
    }
  }, [job, qc])

  const addFieldMutation = useMutation({
    mutationFn: async () => {
      const name = additionalField.name.trim()
      if (!name) throw new Error('Field name is required.')
      const productFamily = additionalField.productFamily.trim() || '*'
      const category = additionalField.category.trim() || 'Technical'
      const aliases = additionalField.aliases
        .split(',')
        .map((alias) => alias.trim())
        .filter(Boolean)

      await upsertTermReference({
        productFamily,
        category,
        canonicalTerm: name,
        alternatives: aliases,
        isCoreTerm: false,
        actor: owner || 'pcr-upload',
      })

      return { productFamily, category, name }
    },
    onSuccess: (field) => {
      setAdditionalFields((current) => [
        field,
        ...current.filter((item) =>
          item.productFamily !== field.productFamily ||
          item.category !== field.category ||
          item.name.toLowerCase() !== field.name.toLowerCase()
        ),
      ])
      setAdditionalField((current) => ({ ...current, name: '', aliases: '' }))
      setMessage(`Added "${field.name}" to ${field.productFamily === '*' ? 'the shared field list' : field.productFamily}.`)
      qc.invalidateQueries({ queryKey: ['term-reference'] })
    },
    onError: (error: any) => {
      setMessage(error?.response?.data?.message || error?.response?.data || error?.message || 'Field could not be added.')
    },
  })

  const selectedRows = useMemo(() => {
    if (!preview) return 0
    return preview.sheets
      .filter((sheet) => selectedSheets[sheet.sheetName] ?? sheet.isDefaultSelected)
      .reduce((sum, sheet) => sum + sheet.rowCount, 0)
  }, [preview, selectedSheets])

  const detectedFieldsByCategory = useMemo(() => {
    if (!preview) return []
    const seen = new Set<string>()
    const groups = new Map<string, string[]>()
    for (const sheet of preview.sheets) {
      for (const mapping of sheet.inferredMappings) {
        if (mapping.targetKind === 'Skip') continue
        const label = mapping.displayLabel || mapping.targetField || mapping.sourceHeader
        const key = label.toLowerCase()
        if (seen.has(key)) continue
        seen.add(key)
        const category = mapping.category?.trim() || 'Other'
        const list = groups.get(category) ?? []
        list.push(label)
        groups.set(category, list)
      }
    }
    return Array.from(groups.entries())
      .map(([category, fields]) => ({ category, fields: fields.sort((a, b) => a.localeCompare(b)) }))
      .sort((a, b) => {
        const ai = schemaCategoryOrder.indexOf(a.category)
        const bi = schemaCategoryOrder.indexOf(b.category)
        return (ai < 0 ? schemaCategoryOrder.length : ai) - (bi < 0 ? schemaCategoryOrder.length : bi)
      })
  }, [preview])

  const normalisationRows = useMemo(() => {
    if (!preview) return []
    return preview.sheets.flatMap((sheet) =>
      sheet.inferredMappings
        .filter((mapping) => mapping.targetKind !== 'Skip' && mapping.sourceHeader !== (mapping.displayLabel || mapping.targetField))
        .slice(0, 12)
        .map((mapping) => ({
          source: mapping.sourceHeader,
          target: mapping.displayLabel || mapping.targetField || mapping.targetKind,
          created: mapping.willCreateCanonicalAttribute,
        }))
    )
  }, [preview])

  const sampleRows = useMemo(() => {
    const sheet = preview?.sheets.find((s) => selectedSheets[s.sheetName] ?? s.isDefaultSelected) ?? preview?.sheets[0]
    if (!sheet) return []
    const headers = sheet.headers.slice(0, 6)
    return sheet.sampleRows.slice(0, 3).map((row) =>
      headers.map((header, index) => ({ header, value: row[index] || '-' }))
    )
  }, [preview, selectedSheets])

  const productFamilyOptions = useMemo(() => {
    const names = new Set<string>()
    preview?.sheets.forEach((sheet) => {
      if (!sheet.isRevisionSheet && sheet.sheetName.trim()) names.add(sheet.sheetName.trim())
    })
    return Array.from(names).sort((a, b) => a.localeCompare(b))
  }, [preview])

  const handleAdditionalFieldSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    addFieldMutation.mutate()
  }

  const pickFile = (nextFile?: File | null) => {
    if (!nextFile) return
    setFile(nextFile)
    previewMutation.mutate(nextFile)
  }

  const handleFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    pickFile(event.target.files?.[0] ?? null)
  }

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setDragOver(false)
    pickFile(event.dataTransfer.files?.[0] ?? null)
  }

  const importRunning = !!jobId && job?.status !== 'Completed' && job?.status !== 'Failed'

  return (
    <MimPage
      title="PCR Upload"
      subtitle="Upload your Excel product master, then map a page and run a website check."
    >
      <div className="mim-two-column">
        <div className="mim-stack">
          <MimCard title="Upload PCR File" subtitle="Excel is the usual source. CSV and JSON are also accepted.">
            <div className="pcr-privacy-banner" role="note">
              <AlertTriangle size={18} />
              <div>
                <strong>Upload only public-labelled content</strong>
                <p>
                  PCR files often mix public marketing facts with non-public technical or certification
                  detail. This tool does not filter confidential rows. You are responsible for uploading
                  only content that is labelled public and suitable to compare against live websites.
                </p>
              </div>
            </div>

            <label
              className={`pcr-dropzone ${dragOver ? 'active' : ''}`}
              onDragOver={(event) => {
                event.preventDefault()
                setDragOver(true)
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <input type="file" accept=".xlsx,.xls,.csv,.json" onChange={handleFileInput} />
              <FileSpreadsheet size={34} />
              <strong>{file ? file.name : 'Drop your PCR file here'}</strong>
              <span>{file ? 'File selected and previewed below' : 'or click to browse'}</span>
              <em>.xlsx, .xls, .csv, .json</em>
            </label>

            <div className="mim-grid-2" style={{ marginTop: 16 }}>
              <div>
                <span className="mim-label">Business division</span>
                <div className="mim-input" style={{ background: 'var(--mim-grey-bg)', cursor: 'default' }}>{divisionLabel}</div>
              </div>
              <div>
                <span className="mim-label">Owner</span>
                <div className="mim-input" style={{ background: 'var(--mim-grey-bg)', cursor: 'default' }}>{owner || '—'}</div>
              </div>
            </div>

            {canApprove ? (
              <label className="pcr-sheet-row" style={{ marginTop: 12, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={approveOnImport}
                  onChange={(e) => setApproveOnImport(e.target.checked)}
                />
                <strong>Import as approved</strong>
                <span>Marks every product in this file as approved, skipping a later PCR snapshot step.</span>
              </label>
            ) : (
              <div className="mim-note" style={{ marginTop: 12 }}>
                <Info size={14} style={{ marginRight: 6 }} />
                This import creates draft products. Approve the PCR on Website Check (step 2) before you run a check.
              </div>
            )}

            <div className="mim-note" style={{ marginTop: 14 }}>
              <Info size={14} style={{ marginRight: 6 }} />
              {isGroupUser
                ? 'Group access still sees every division. PCR imports are stored in the division on your profile.'
                : 'Division and owner come from your user profile. Complete your profile if they are missing.'}
            </div>

            {!profileReady && (
              <div className="pcr-privacy-banner" style={{ marginTop: 12, marginBottom: 0 }}>
                <AlertTriangle size={18} />
                <div>
                  <strong>Complete your profile before importing</strong>
                  <p>Choose your division or business entity in the profile prompt so products are stored in the right scope.</p>
                </div>
              </div>
            )}

            {message && (
              <div className={`pcr-message${isErrorMessage(message, job?.status, job?.errorMessage) ? ' is-error' : ''}`}>
                {message}
              </div>
            )}

            {(importRunning || job) && (
              <div className="pcr-import-progress" style={{ marginTop: 14, padding: 14, border: '1px solid var(--mim-border)', borderRadius: 8, background: '#fffbeb' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: '#92400e' }}>
                  <AlertTriangle size={16} />
                  <strong>Do not leave this page while the import is running.</strong>
                </div>
                <div style={{ marginBottom: 6 }}>
                  {job?.currentStep || 'Queued'} — {job?.percent ?? 0}%
                  {job?.totalRows
                    ? ` (${job.processedRows}/${job.totalRows}${
                        String(job.currentStep || '').startsWith('FactSync')
                          ? ' products'
                          : String(job.currentStep || '').startsWith('Approving')
                            ? ' products'
                            : ' rows'
                      })`
                    : ''}
                </div>
                <div style={{ height: 8, background: '#fde68a', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{ width: `${job?.percent ?? 0}%`, height: '100%', background: 'var(--mim-blue)', transition: 'width 0.3s' }} />
                </div>
                {(job?.currentProductCode || job?.currentProductName) && (
                  <div style={{ marginTop: 8, fontSize: 13, color: 'var(--mim-muted)' }}>
                    Importing: <strong>{job.currentProductCode}</strong>
                    {job.currentProductName ? ` — ${job.currentProductName}` : ''}
                  </div>
                )}
              </div>
            )}

            {preview && (
              <div className="pcr-import-actions">
                <div>
                  <strong>{selectedRows}</strong>
                  <span> selected rows</span>
                </div>
                <button
                  type="button"
                  className="mim-btn mim-btn-primary"
                  disabled={importMutation.isPending || importRunning || !profileReady}
                  onClick={() => importMutation.mutate()}
                >
                  <Upload size={14} />
                  {importRunning ? 'Importing...' : importMutation.isPending ? 'Starting...' : 'Import PCR'}
                </button>
              </div>
            )}

            {result && (
              <div className="pcr-result-grid">
                <div><strong>{result.productsCreated + result.productsUpdated}</strong><span>Products</span></div>
                <div><strong>{result.dataItemsCreated + result.dataItemsUpdated}</strong><span>Facts</span></div>
                <div><strong>{result.canonicalAttributesCreated}</strong><span>New fields</span></div>
                <div><strong>{result.productsApproved}</strong><span>Approved</span></div>
                <div><strong>{result.errors.length}</strong><span>Warnings</span></div>
              </div>
            )}

            {result && (
              <div className="pcr-next-steps">
                {result.productsApproved === 0 && (
                  <div className="mim-note">
                    Products are saved as drafts. Open Website Check, select a product, and create the approved PCR snapshot, then add the page URL.
                  </div>
                )}
                <Link className="mim-btn mim-btn-primary" to="/compliance-check">
                  Start a website check <ArrowRight size={14} />
                </Link>
                <Link className="mim-btn mim-btn-secondary" to="/pcr-management">
                  View Product Register <ArrowRight size={14} />
                </Link>
              </div>
            )}
          </MimCard>

          {preview && (
            <MimCard title="Field Normalisation Log" subtitle="Detected source columns mapped to canonical register fields.">
              <div className="mim-stack">
                {normalisationRows.length === 0 && (
                  <EmptyState title="No field aliases detected" detail="The preview mappings already match canonical fields." />
                )}
                {normalisationRows.map((row, index) => (
                  <div className="pcr-normalisation-row" key={`${row.source}-${index}`}>
                    <StatusPill status={row.created ? 'New Field' : 'Normalised'} tone={row.created ? 'warning' : 'info'} />
                    <span>{row.source}</span>
                    <ArrowRight size={12} />
                    <strong>{row.target}</strong>
                  </div>
                ))}
              </div>
            </MimCard>
          )}
        </div>

        <div className="mim-stack">
          <MimCard
            title="Detected Field Schema"
            subtitle="Only fields actually found in this file are shown."
          >
            {!preview ? (
              <EmptyState title="No file uploaded yet" detail="Upload a PCR file to see the fields it actually contains." />
            ) : detectedFieldsByCategory.length === 0 ? (
              <EmptyState title="No fields detected" detail="None of the columns in this file matched a known or creatable field." />
            ) : (
              <div className="pcr-schema-grid">
                {detectedFieldsByCategory.map((group) => (
                  <div className="pcr-schema-card" key={group.category}>
                    <h3>{group.category}</h3>
                    {group.fields.map((field) => (
                      <div key={field}>
                        <CheckCircle2 size={12} />
                        <span>{field}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {additionalFields.length > 0 && (
              <div className="pcr-manual-field-list">
                {additionalFields.map((field) => (
                  <span key={`${field.productFamily}-${field.category}-${field.name}`}>
                    {field.name} <em>{field.productFamily === '*' ? 'shared' : field.productFamily}</em>
                  </span>
                ))}
              </div>
            )}

            <details className="pcr-add-field-panel">
              <summary><PlusCircle size={14} /> Add additional field</summary>
              <form className="pcr-add-field-form" onSubmit={handleAdditionalFieldSubmit}>
                <label>
                  <span className="mim-label">Field name</span>
                  <input
                    className="mim-input"
                    value={additionalField.name}
                    onChange={(e) => setAdditionalField((current) => ({ ...current, name: e.target.value }))}
                    placeholder="e.g. Acoustic rating"
                  />
                </label>
                <div className="mim-grid-2">
                  <label>
                    <span className="mim-label">Product family</span>
                    <input
                      className="mim-input"
                      list="pcr-product-families"
                      value={additionalField.productFamily}
                      onChange={(e) => setAdditionalField((current) => ({ ...current, productFamily: e.target.value }))}
                      placeholder="Shared (*)"
                    />
                    <datalist id="pcr-product-families">
                      {productFamilyOptions.map((family) => <option key={family} value={family} />)}
                    </datalist>
                  </label>
                  <label>
                    <span className="mim-label">Category</span>
                    <select
                      className="mim-select"
                      value={additionalField.category}
                      onChange={(e) => setAdditionalField((current) => ({ ...current, category: e.target.value }))}
                    >
                      {additionalFieldCategories.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <label>
                  <span className="mim-label">PCR aliases</span>
                  <input
                    className="mim-input"
                    value={additionalField.aliases}
                    onChange={(e) => setAdditionalField((current) => ({ ...current, aliases: e.target.value }))}
                    placeholder="Optional comma-separated PCR column names"
                  />
                </label>
                <div className="pcr-add-field-actions">
                  <span>Leave product family blank for a shared field.</span>
                  <button type="submit" className="mim-btn mim-btn-secondary" disabled={addFieldMutation.isPending}>
                    {addFieldMutation.isPending ? 'Adding...' : 'Add field'}
                  </button>
                </div>
              </form>
            </details>
          </MimCard>

          {preview && (
            <MimCard
              title={`Product Preview (${sampleRows.length} rows)`}
              actions={<Link to="/pcr-management" className="mim-btn mim-btn-secondary">View all <ArrowRight size={13} /></Link>}
            >
              {preview.sheets.map((sheet) => (
                <label key={sheet.sheetName} className="pcr-sheet-row">
                  <input
                    type="checkbox"
                    checked={selectedSheets[sheet.sheetName] ?? sheet.isDefaultSelected}
                    onChange={(e) => setSelectedSheets((current) => ({ ...current, [sheet.sheetName]: e.target.checked }))}
                  />
                  <strong>{sheet.sheetName}</strong>
                  <span>{sheet.rowCount} rows, {sheet.inferredMappings.filter((m) => m.targetKind !== 'Skip').length} mapped fields</span>
                </label>
              ))}

              <div className="mim-table-wrap" style={{ marginTop: 14 }}>
                <table className="mim-table">
                  <thead>
                    <tr>
                      {(sampleRows[0] ?? []).map((cell) => <th key={cell.header}>{cell.header}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {sampleRows.map((row, index) => (
                      <tr key={index}>
                        {row.map((cell) => <td key={`${index}-${cell.header}`}>{cell.value}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </MimCard>
          )}
        </div>
      </div>
    </MimPage>
  )
}

function importErrorMessage(error: any, fallback = 'Import failed.') {
  const status = error?.response?.status
  const apiMessage = error?.response?.data?.message || error?.response?.data?.detail || error?.response?.data
  const text = typeof apiMessage === 'string' ? apiMessage : ''
  if (status === 403) {
    return text || 'You do not have access to import into this division. Check your profile scope and try again.'
  }
  if (!status) {
    return 'The import request did not reach the server. Preview the file again, then import. If it still fails, the workbook may be too large for one upload.'
  }
  return text || error?.message || fallback
}

function isErrorMessage(message: string, jobStatus?: string, jobError?: string) {
  if (jobStatus === 'Failed' || jobError) return true
  const lower = message.toLowerCase()
  return lower.includes('fail') || lower.includes('denied') || lower.includes('error') || lower.includes('could not')
}
